import {useInfiniteQuery, useMutation, useQuery, useQueryClient} from 'react-query';
import {
    addKnowledgeTopic,
    addKnowledgeRssSource,
    fetchKnowledgeFeedEntries,
    fetchKnowledgeFollowingContents,
    fetchSavedKnowledgeLessons,
    fetchKnowledgeTopics,
    generateKnowledgeLesson,
    refreshAllFeeds,
    saveKnowledgeFeedEntry,
    saveKnowledgeLesson,
    unfollowKnowledgeFeed,
} from '../api/knowledgeFeedApi';

export function useKnowledgeFeedEntries(token) {
    return useQuery({
        queryKey: ['knowledgeFeedEntries'],
        queryFn: () => fetchKnowledgeFeedEntries(token),
        enabled: !!token,
        initialData: [],
    });
}

export function useKnowledgeFollowingContents(token) {
    return useQuery({
        queryKey: ['knowledgeFollowingContents'],
        queryFn: () => fetchKnowledgeFollowingContents(token),
        enabled: !!token,
        initialData: [],
    });
}

export function useAddKnowledgeRssSource(token) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (rssUrl) => addKnowledgeRssSource(token, rssUrl),
        onSuccess: () => {
            queryClient.resetQueries({queryKey: ['knowledgeFeedPages']});
            queryClient.invalidateQueries({queryKey: ['knowledgeFollowingContents']});
        },
    });
}

export function useKnowledgeFeedPages(token) {
    return useInfiniteQuery({
        queryKey: ['knowledgeFeedPages'],
        queryFn: ({pageParam = null}) => refreshAllFeeds(token, pageParam),
        enabled: !!token,
        getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.nextCursor : undefined,
        retry: (failureCount, error) => error?.status !== 400 && failureCount < 2,
    });
}

export function useSaveKnowledgeFeedEntry(token) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (entry) => saveKnowledgeFeedEntry(token, entry),
        onSuccess: (savedEntry) => {
            queryClient.setQueryData(['knowledgeFeedEntries'], (previous = []) => [
                savedEntry,
                ...previous.filter((entry) => entry.id !== savedEntry.id),
            ]);
        },
    });
}

export function useUnfollowKnowledgeFeed(token) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (rssUrl) => unfollowKnowledgeFeed(token, rssUrl),
        onSuccess: () => {
            queryClient.resetQueries({queryKey: ['knowledgeFeedPages']});
            queryClient.invalidateQueries({queryKey: ['knowledgeFollowingContents']});
        },
    });
}

export function useKnowledgeTopics(token) {
    return useQuery({
        queryKey: ['knowledgeTopics'],
        queryFn: () => fetchKnowledgeTopics(token),
        enabled: !!token,
        initialData: [],
    });
}

export function useAddKnowledgeTopic(token) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (topic) => addKnowledgeTopic(token, topic),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['knowledgeTopics']});
        },
    });
}

export function useSavedKnowledgeLessons(token) {
    return useQuery({
        queryKey: ['savedKnowledgeLessons'],
        queryFn: () => fetchSavedKnowledgeLessons(token),
        enabled: !!token,
        initialData: [],
    });
}

export function useGenerateKnowledgeLesson(token) {
    return useMutation({
        mutationFn: (topicId) => generateKnowledgeLesson(token, topicId),
    });
}

export function useSaveKnowledgeLesson(token) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (lesson) => saveKnowledgeLesson(token, lesson),
        onSuccess: (savedLesson) => {
            queryClient.setQueryData(['savedKnowledgeLessons'], (previous = []) => [
                savedLesson,
                ...previous.filter((lesson) => lesson.id !== savedLesson.id),
            ]);
        },
    });
}
